<script setup lang="ts">
  import { onBeforeMount, ref, watch } from 'vue';

  import type { Email, EmailThread, Task } from '@stuut/api/models';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { cn } from '@ui/utils/utils.ts';

  import { stuut } from '@/api';
  import EmailBody from '@/components/emails/EmailBody.vue';
  import EmailEditor from '@/components/emails/EmailEditor.vue';
  import EmailHeader from '@/components/emails/EmailHeader.vue';

  interface Props {
    email: Email;
    hideReply?: boolean;
    initialCollapse?: boolean;
    isInitialDraft?: boolean;
    /** Another reply to the same parent follows — keep the shared container open and add a spacer below this card. */
    mergeDown?: boolean;
    processing?: boolean;
    showSignature?: boolean;
    size?: '2xs' | 'xs' | 'sm';
    thread?: EmailThread;
  }

  const props = defineProps<Props>();
  const emits = defineEmits<{
    (e: 'deleted'): void;
    (e: 'updated'): void;
    (e: 'draftCreated', draft: Email): void;
    (e: 'openTask', task: Task): void;
  }>();

  const isLoading = ref(false);
  const isCollapsed = ref(props.initialCollapse);
  const hasBeenExpanded = ref(!props.initialCollapse);

  watch(isCollapsed, (collapsed) => {
    if (!collapsed) hasBeenExpanded.value = true;
  });

  const internalThread = ref<EmailThread | null>(props.thread || props.email.thread || null);

  onBeforeMount(async () => {
    if (!internalThread.value) {
      internalThread.value = await stuut.emailThreads.retrieve(props.email.thread_id);
    }
  });

  watch(
    () => props.email.isSending,
    (isSending, _, onCleanup) => {
      if (!isSending) return;
      const interval = setInterval(() => stuut.emails.retrieve(props.email.id, props.email), 2000);
      onCleanup(() => clearInterval(interval));
    },
    { immediate: true },
  );

  const handleDraftCreated = (draft: Email) => {
    emits('draftCreated', draft);
  };
</script>

<template>
  <div
    v-if="email.isSent || email.isSending"
    v-bind="$attrs"
    :class="
      cn(
        'min-w-85 relative flex flex-col transition-colors border-l duration-150',
        isCollapsed ? 'cursor-pointer bg-background' : 'pb-6',
        email.isDraft ? 'mt-5' : '',
        (!size || ['2xs', 'xs'].includes(size)) && 'py-4',
      )
    "
  >
    <EmailHeader
      v-if="internalThread"
      :email="email"
      :thread="internalThread"
      class="cursor-pointer h-fit pt-4.5 pb-6 px-6"
      :class="isCollapsed && 'hover:bg-muted/50'"
      :collapsed="isCollapsed"
      :hide-reply
      :processing
      :size
      @click="isCollapsed = !isCollapsed"
      @draft-created="handleDraftCreated"
    >
      <template #after_direction_badge>
        <slot name="email_header" v-bind="{ isCollapsed }" />
      </template>

      <template #header_right>
        <slot name="header_right" v-bind="{ isCollapsed }" />
      </template>
    </EmailHeader>

    <div
      class="grid transition-[grid-template-rows] duration-200 ease-out"
      :style="{ gridTemplateRows: isCollapsed ? '0fr' : '1fr' }"
    >
      <div class="overflow-hidden min-h-0 flex flex-col">
        <div class="flex flex-col">
          <EmailBody v-if="hasBeenExpanded" :email="email" :size :show-signature />
        </div>
      </div>
    </div>

    <slot name="after_body" v-bind="{ isCollapsed }"></slot>

    <div
      v-if="isLoading"
      class="h-full bg-secondary w-full flex flex-col items-center justify-center absolute top-0 left-0"
    >
      <div>
        <LoadingSpinner class="text-lg" />
      </div>
    </div>
  </div>

  <div
    v-else-if="internalThread"
    class="relative flex flex-col"
    :class="
      isInitialDraft
        ? ''
        : cn('border-l px-6', mergeDown ? 'pb-3' : 'rounded-b-md border-b-1 mb-3 pb-8')
    "
    v-bind="$attrs"
  >
    <div
      class="flex flex-col bg-background"
      :class="isInitialDraft ? 'rounded-md border-l' : 'border rounded-lg shadow-md'"
    >
      <EmailEditor
        :email="email"
        :thread="internalThread"
        @deleted="emits('deleted')"
        @updated="emits('updated')"
      />
    </div>
  </div>
</template>

<style scoped lang="postcss"></style>
